import React, { useState, FC, useEffect } from 'react';
import { useTranslation } from '@blocs.i18n';
import { useExternalScript, WindowWidthContext } from '@minecraft.utils';
import { BasicContainer, FlexCol, FlexDynamic, H1Text, H2Text, Loader } from '@minecraft.atoms';
import Modal, { useModal } from '@minecraft.modal';
import Loading from '@minecraft.loader';
import { getEnvironment } from '@minecraft.environment';
import { RecurlyProvider } from '@recurly/react-recurly';
import { AddPaymentMethod } from '../Checkout/AddPaymentMethod';
import { AddCard, CreditCard } from '../CreditCard';
import { useUserContext } from '../../Auth';
import { useBillingPopup } from '../BillingPopupContext';
import { captureExceptionEvent } from '../../Sentry';
import { ThreeDSecureForm, ThreeDActionType } from '../ThreeDSecureFlow';

export type CreditCardFormProps = {
  title?: string;
  isUpdateBillingInfo?: boolean;
  isAddModal?: boolean;
  className?: string;
  omitTitle?: boolean;
};

const addNewState = 'addNewState';

export const CreditCardForm: FC<CreditCardFormProps> = ({
  title,
  isUpdateBillingInfo = false,
  isAddModal = false,
  className,
  omitTitle,
}): JSX.Element => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();

  const config = getEnvironment();
  const billingPlatformPublicKey = config['RECURLY_PUBLIC_KEY'];
  const [externalScriptLoaded, loadBillingScriptError] = useExternalScript('https://js.recurly.com/v4/recurly.js');

  const { isOpen: removeModalOpen, toggleModal: toggleRemovePaymentModal } = useModal(false);

  const [selectedCard, setSelectedCard] = useState(null);
  const ctaLabel = selectedCard?.id ? t('common:button.update') : t('common:button.add');

  const { myAccount, subscription } = useUserContext();
  const countryCode = myAccount?.country?.code;
  const currencyCode = subscription?.plan?.currency?.code;

  const {
    onAddPaymentOption,
    addPaymentLoading,
    addPaymentError,
    onRemovePaymentOption,
    removePaymentLoading,
    onUpdatePaymentOption,
    updatePaymentLoading,
    onThreeDActionError,
    threeDActionContext,
    wallet,
    walletLoading,
  } = useBillingPopup();

  useEffect(() => {
    if (wallet && !selectedCard && !isAddModal) {
      setSelectedCard(wallet.find((card) => card.isPrimaryPaymentMethod) || wallet[0]);
    }
  }, [wallet, selectedCard, isAddModal]);

  return (
    <Loader
      loading={!externalScriptLoaded}
      error={loadBillingScriptError && t('common:globalErrors.generic')}
      LoaderComponent={<Loading />}
    >
      <RecurlyProvider publicKey={billingPlatformPublicKey} required={['cvv', 'postal_code']}>
        <Loader
          loading={walletLoading || updatePaymentLoading || addPaymentLoading || Boolean(threeDActionContext?.token)}
          LoaderComponent={<Loading />}
        >
          <BasicContainer className={className} m="4">
            {!isMobile && title && <H1Text>{title}</H1Text>}
            <FlexDynamic
              direction="column"
              alignItems="start"
              justifyContent="center"
              gap="4"
              mdProps={{
                alignItems: 'flex-start',
                justifyContent: 'space-evenly',
                direction: 'row',
              }}
              data-testid="wallet-container"
            >
              {!isAddModal && (
                <FlexCol gap="2" grow={0} shrink={0}>
                  {isUpdateBillingInfo && (
                    <BasicContainer my="4">
                      <H2Text>{t('common:billing.wallet')}</H2Text>
                    </BasicContainer>
                  )}
                  {wallet?.map((card) => (
                    <CreditCard
                      isSelected={card.id === selectedCard?.id}
                      isPrimary={card.isPrimaryPaymentMethod}
                      isBackup={card.isBackupPaymentMethod}
                      cardType={card.cardType}
                      paymentType={card.paymentType}
                      lastFour={card.lastFour}
                      onSelect={() => setSelectedCard(card)}
                      key={card.id}
                    />
                  ))}
                  <AddCard onSelect={() => setSelectedCard(addNewState)} />
                </FlexCol>
              )}
              <AddPaymentMethod
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                onAddPaymentOption={onAddPaymentOption}
                onUpdatePaymentOption={onUpdatePaymentOption}
                addPaymentError={addPaymentError}
                onCancel={() => null}
                toggleRemovePaymentModal={toggleRemovePaymentModal}
                threeDActionContext={threeDActionContext}
                onThreeDActionError={onThreeDActionError}
                countryCode={countryCode}
                currencyCode={currencyCode}
                addPaymentLoading={addPaymentLoading}
                ctaLabel={ctaLabel}
                omitTitle={omitTitle}
                allowCancel={false}
                showAuthorizeMessage
                w={isMobile || isAddModal ? 'full' : '1/3'}
                borderPlacement={isAddModal ? 'none' : 'all'}
                padding={isAddModal ? 0 : 4}
                bgColor="card"
              />
            </FlexDynamic>
            <Modal
              data-testid="remove-billing-info-modal"
              isOpen={removeModalOpen}
              modalSize="small"
              cancelLabel={t('common:button.cancel')}
              submitLabel={t('common:button.confirm')}
              handleSubmitClick={async () => {
                await onRemovePaymentOption({ billingInfoId: selectedCard?.id });
                setSelectedCard(null);
                toggleRemovePaymentModal();
              }}
              handleToggleClick={() => toggleRemovePaymentModal()}
              title={t('common:billing.card.delete.title')}
              overlayLoading={removePaymentLoading}
            />
          </BasicContainer>
        </Loader>
        {threeDActionContext?.token && (
          <ThreeDSecureForm
            threeDActionToken={threeDActionContext.token}
            onToken={async (token) => {
              try {
                if (threeDActionContext.actionType === ThreeDActionType.ADD_PAYMENT) {
                  await onAddPaymentOption({ threeDSecureActionResultTokenId: token.id });
                }

                if (threeDActionContext.actionType === ThreeDActionType.UPDATE_PAYMENT) {
                  await onUpdatePaymentOption({
                    billingInfoId: threeDActionContext?.selectedBillingInfoId,
                    threeDSecureActionResultTokenId: token.id,
                  });
                }
              } catch (error) {
                // we do not want to display the error notification if the 3DS flow is required
                if (error?.graphQLErrors?.[0]?.data?.errorData?.threeDSecureActionTokenId) return;

                captureExceptionEvent({ location: 'Talent -> 3DS Complete Purchase', error });
                onThreeDActionError();
              }
            }}
            onError={onThreeDActionError}
            loading={addPaymentLoading}
          />
        )}
      </RecurlyProvider>
    </Loader>
  );
};
